// The picker's own email to the commissioner. The app never sends this --
// docs/LEAGUE.md: that email is the official submission system, and this app
// has no outbound mail by design (see CLAUDE.md's Postmark note). All this
// does is build a pre-filled Gmail compose URL the picker opens, reviews, and
// sends from their own account, so it goes out as them.
//
// The subject carries the week and the picker's name; the body is just the
// five team names, one per line. No spread, no matchup, no header.

export const PICKS_RECIPIENT = "FivePicks2019@gmail.com";

// picks: an array of team names (full names, e.g. "New York Giants"), already
// in the order they should appear.
export function composePickEmail({ week, pickerName, picks }) {
  const name = (pickerName || "").trim();
  const subject = name ? `Week ${week} - ${name}` : `Week ${week}`;
  const body = picks.join("\n");
  return { to: PICKS_RECIPIENT, subject, body };
}

// Gmail's web compose deep link. `view=cm&fs=1` opens a full compose window;
// `to`/`su`/`body` prefill it. URLSearchParams handles the encoding, including
// the newlines in the body.
//
// `authuser` is not optional in practice. Without it Gmail composes from
// whatever account is default in that browser -- account 0 -- which for anyone
// signed into more than one Google account is a coin flip, and the pool's
// standings are keyed on the address the mail arrives from. Sending from the
// wrong account does not bounce, it just quietly files the picks under nobody.
// We know the right address: it is the Google account that signed in to this
// app, so pin the compose to it. Gmail accepts a full email address here and
// switches accounts (or prompts) rather than silently using the default.
export function gmailComposeUrl({ to, subject, body }, authuser) {
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to,
    su: subject,
    body,
  });
  if (authuser) params.set("authuser", authuser);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

// A `mailto:` link, for handing the compose off to whatever mail app the
// device has. Note the encoding differs from the Gmail link above:
// URLSearchParams writes a space as "+", which Gmail's web compose decodes but
// a mail client does not -- it would put literal plus signs in the subject --
// so these are encodeURIComponent'd instead.
export function mailtoUrl({ to, subject, body }) {
  const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${encodeURIComponent(to)}?${query}`;
}

// Gmail's own app deep link. On iOS a mail.google.com URL is claimed by the
// installed Gmail app, which opens the inbox and drops the compose parameters
// on the floor; `googlegmail:///co` is the form it actually honours. There is
// no way to detect whether the scheme is registered, so this is only ever
// offered as an explicit choice, never auto-selected -- an unregistered scheme
// navigates to nothing and the button looks broken again.
export function gmailAppUrl({ to, subject, body }) {
  const query =
    `to=${encodeURIComponent(to)}` +
    `&subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`;
  return `googlegmail:///co?${query}`;
}

// Used to ORDER the choices, never to make them. See `openPickEmail`: sniffing
// the UA and then committing to one hand-off is what produced both failures
// here -- a desktop compose under the wrong Google account, and a `mailto:`
// that the OS handed to whatever it had registered, which on a Mac with no
// configured mail client is the default browser.
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPod/i.test(ua)) return true;
  // iPadOS reports a desktop UA; it is a touch device with no mouse.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

export function pickEmailUrl(fields, authuser) {
  return isMobileDevice() ? mailtoUrl(fields) : gmailComposeUrl(fields, authuser);
}

// The always-works fallback. Every hand-off above depends on something outside
// this app -- which Google account a browser defaults to, which app owns
// `mailto:`, whether a custom scheme is registered -- and each of those has now
// failed on a real device. A clipboard copy depends on nothing: the picker
// opens whatever mail client they were going to use anyway and pastes.
export function pickEmailText({ to, subject, body }) {
  return `To: ${to}\nSubject: ${subject}\n\n${body}`;
}
